import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { GenericEventFeed } from './GenericEventFeed';
import { useQueryResultsPagination } from './hooks/useQueryResultsPagination';
import { useAddEventToReportMenuItem, useOpenInNewWindow } from '@/hooks';
import { useQueryResultsStore } from '@/stores';
import { getDetectionLabel } from '@/utils/detection-classes';
import type { FeedEmptyStateProps } from './components';

interface QueryResultsFeedProps {
    eventId: string;
    titleContent?: ReactNode;
    emptyState?: FeedEmptyStateProps;
}

export function QueryResultsFeed({
    eventId,
    titleContent,
    emptyState,
}: QueryResultsFeedProps) {
    const { t } = useTranslation(I18nNamespace.EVENTS);
    const { openEvent } = useOpenInNewWindow();
    const addToReportItem = useAddEventToReportMenuItem();
    const query = useQueryResultsStore((state) => state.lastQueryParams?.text);
    const { events, hasMore, isLoadingMore, loadMore } =
        useQueryResultsPagination();

    return (
        <GenericEventFeed
            containerTitle={t('sidebar.queryResults')}
            titleContent={titleContent}
            showViewAll={false}
            items={events}
            isLoading={false}
            emptyState={emptyState}
            itemKey={(scored) => scored.event.id}
            itemEvent={(scored) => scored.event}
            cardTitle={t('page.event')}
            itemDescription={(scored) =>
                getDetectionLabel(t, scored.event.classId)
            }
            itemConfidence={(scored) => scored.confidence}
            itemActionMenuItems={(scored, event) =>
                addToReportItem(event, {
                    query,
                    confidence: scored.confidence,
                })
            }
            onNavigate={(scored, clickEvent) =>
                openEvent(scored.event.id, clickEvent, {
                    state: {
                        from: 'query-results',
                        feedContext: { type: 'queryResults', eventId },
                    },
                })
            }
            quickViewContextType="query-results"
            pagination={{
                hasMore,
                isLoadingMore,
                onLoadMore: loadMore,
                mode: 'loadMore',
            }}
        />
    );
}
